import { useState, useEffect, useCallback } from 'react';

const CLOUD_API = 'https://api.yoohuo.fun';
const POLL_INTERVAL = 30000; // 30s

export interface Device {
  machine_id: string;
  machine_name: string | null;
  tunnel_url: string;
  access_token: string;
  online: boolean;
  last_heartbeat: number;
}

export function useDeviceDiscovery(getToken: () => string | null) {
  const [devices, setDevices] = useState<Device[]>([]);
  const [activeDevice, setActiveDevice] = useState<Device | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDevices = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }

    try {
      const res = await fetch(`${CLOUD_API}/api/tunnel/devices`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const devs: Device[] = data.devices || [];
        setDevices(devs);
        setError(null);

        const onlineDevs = devs.filter(d => d.online);

        // If we have an active device, check if its URL changed (tunnel restarted)
        if (activeDevice) {
          const updated = onlineDevs.find(d => d.machine_id === activeDevice.machine_id);
          if (updated && updated.tunnel_url !== activeDevice.tunnel_url) {
            console.log('[Discovery] Tunnel URL changed, reconnecting');
            setActiveDevice(updated);
          } else if (!updated) {
            // Device went offline
            setActiveDevice(null);
          }
        } else {
          // Auto-select: single online device → connect automatically
          if (onlineDevs.length === 1) {
            setActiveDevice(onlineDevs[0]);
          }
        }
      } else if (res.status === 401) {
        setError('Session expired');
      }
    } catch {
      setError('Network error');
    }
    setLoading(false);
  }, [getToken, activeDevice]);

  useEffect(() => {
    fetchDevices();
    const interval = setInterval(fetchDevices, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchDevices]);

  const selectDevice = useCallback((device: Device) => {
    setActiveDevice(device);
  }, []);

  return { devices, activeDevice, loading, error, selectDevice, refresh: fetchDevices };
}
